/**
 * Carrier MCP — Tool: list_recent_ocs_events
 *
 * Reads the per-ICCID ring-buffer written by the OCS webhook receiver
 * (ocs-webhook.ts → storeEvent). Key pattern:
 *   events:<reseller_id>:<iccid>:evt:<padded_ts>:<event_id>  (primary, per-event)
 *   events:<reseller_id>:<iccid>                             (legacy batch, read-compat)
 *
 * Core implementation: @carrier/ocs-spec/ocs-event-buffer-read
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { Env } from "./types.js";
import type { ToolContext } from "./tools.js";
import { assertManagedIccidAccess, managedOwnAccountId } from "./ocs-scoping.js";
import { requireBinding } from "./binding-unavailable.js";
import {
  listRecentOcsEvents as listRecentOcsEventsCore,
  type ListRecentOcsEventsResult,
  type OcsEventOutput,
} from "@carrier/ocs-spec/ocs-event-buffer-read";

export type { ListRecentOcsEventsResult, OcsEventOutput };

/**
 * Selectable event types. Mirrors apps/mcp-server/src/list-recent-ocs-events.ts;
 * both MUST track the `OcsEventType` union in ocs-webhook.ts, which is what the
 * writers actually emit. See that file for why this list had drifted.
 */
const ALLOWED_EVENT_TYPES = [
  "esim.activated",
  "esim.disabled",
  "esim.enabled",
  "package.low_balance",
  "package.low_quota",
  "package.expiring",
  "package.expired",
  "location.changed",
  "country.entered",
  "usage.anomaly",
] as const;

export const listRecentOcsEventsSchema = {
  iccid: z
    .string()
    .regex(/^\d{19,20}$/)
    .describe("ICCID, 19 or 20 digits, ITU-T E.118 format"),
  limit: z
    .number()
    .int()
    .min(1)
    .max(50)
    .default(20)
    .describe("Max events to return, newest first"),
  event_types: z
    .array(z.enum(ALLOWED_EVENT_TYPES))
    .optional()
    .describe("Filter to specific event types"),
  since: z
    .string()
    .datetime()
    .optional()
    .describe("ISO-8601 timestamp; only events after this point"),
};

export async function listRecentOcsEvents(
  iccid: string,
  limit: number,
  eventTypes: readonly string[] | undefined,
  since: string | undefined,
  kv: Env["OCS_EVENT_ROUTING"],
): Promise<ListRecentOcsEventsResult> {
  return listRecentOcsEventsCore(iccid, limit, eventTypes, since, kv);
}

export function registerListRecentOcsEventsTool(
  server: McpServer,
  ctx: ToolContext,
): void {
  const env: Env = ctx.env;
  server.registerTool(
    "list_recent_ocs_events",
    {
      title: "List Recent OCS Events",
      description:
        "Return the last N OCS events buffered for a given ICCID. Events include eSIM activations, disable, package expiry warnings, location changes, and balance alerts. Location for every SIM under reseller 1170 (all child accounts) lands on this buffer; each event may include account_id. Buffer holds up to 50 events per ICCID, 24h TTL. Use this when a user asks 'what happened to ICCID X recently' or 'why did subscriber Y go offline'.",
      inputSchema: listRecentOcsEventsSchema,
      annotations: {
        readOnlyHint: true,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async (args) => {
      const { iccid, limit, event_types, since } = args as {
        iccid: string;
        limit: number;
        event_types?: Array<(typeof ALLOWED_EVENT_TYPES)[number]>;
        since?: string;
      };

      try {
        // The ring-buffer key takes its reseller from the `iccid:<iccid>`
        // routing entry, not from the caller, so the KV read on its own answers
        // for any ICCID in the fleet. Managed orgs share one master token, so
        // without this preflight one customer can read another customer's
        // activation, location and balance-alert history.
        if (managedOwnAccountId(ctx.managedAuth) !== undefined) {
          const access = await assertManagedIccidAccess(
            env.CARRIER_OCS_BASE_URL,
            { token: await ctx.getUserToken(ctx.props.sub), ...ctx.managedAuth },
            iccid,
          );
          if (!access.ok) {
            return {
              isError: true,
              content: [{ type: "text", text: access.error }],
            };
          }
        }

        // Guarded before the read: the ring buffer is filled by the hosted
        // webhook receiver as events arrive from the network. A local server
        // receives none, and reading `.get` off the null binding threw a
        // TypeError that surfaced as "Error reading OCS event buffer: Cannot
        // read properties of null" — which reads as a corrupt buffer rather
        // than an absent one.
        requireBinding(
          env.OCS_EVENT_ROUTING,
          "list_recent_ocs_events",
          "OCS_EVENT_ROUTING",
          "the OCS event ring buffer",
        );

        const result = await listRecentOcsEvents(
          iccid,
          limit,
          event_types,
          since,
          env.OCS_EVENT_ROUTING,
        );
        return {
          content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return {
          isError: true,
          content: [{ type: "text", text: `Error reading OCS event buffer: ${message}` }],
        };
      }
    },
  );
}
